import React from "react"
import styled, {keyframes} from "styled-components"
import Layout from "../Layout";
import {Center, PageTitle} from "../shared/mix";
import SEO from "../SEO";

const skills = [
    "JavaScript", "TypeScript", "React", "Next.js", "Node.js", "Redux",
    "PHP", "Laravel", "Astro", "Angular",
    "Styled Components", "Sass", "CSS",
    "Git", "REST API", "MySQL",
    "Java", "C#",
    "Figma", "Adobe Illustrator", "Photoshop",
    "DNS", "Sítě", "Linux",
]

const CV = () => {
    return (
        <Layout>
            <SEO
                title="CV — Životopis"
                description="Životopis Matěje Půhoného — web developer z Pardubic. JavaScript, TypeScript, React, Next.js, Node.js, PHP, Laravel. Freelancer od 2019, spolupráce s Nextap, mezinárodní zkušenosti."
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "ProfilePage",
                    "name": "CV — Matěj Půhoný",
                    "mainEntity": {
                        "@type": "Person",
                        "name": "Matěj Půhoný",
                        "jobTitle": "Web developer",
                        "knowsAbout": ["JavaScript", "TypeScript", "React", "Next.js", "Node.js", "PHP", "Laravel", "Astro", "Angular", "Git", "MySQL", "Figma"],
                        "alumniOf": [
                            {"@type": "CollegeOrUniversity", "name": "Fakulta informatiky a managementu UHK"},
                            {"@type": "CollegeOrUniversity", "name": "České vysoké učení technické v Praze"},
                            {"@type": "CollegeOrUniversity", "name": "VIVES University, Belgie"}
                        ],
                        "award": [
                            "Pardubický talent 2022",
                            "Ocenění Pardubického kraje — BurzaŠkol",
                            "2. místo — Ústřední kolo programování web 2021",
                            "1. místo — Krajské kolo programování web 2021",
                            "Mladý řemeslník roku 2019"
                        ]
                    }
                }}
            />
            <CvCenter>
                <Header>
                    <Avatar />
                    <HeaderInfo>
                        <Name>Matěj Půhoný</Name>
                        <Role>Web developer</Role>
                        <HeaderMeta>
                            <MetaItem>
                                <i className="fa-solid fa-location-dot" />
                                Pardubice, Hradec Králocé, Česká republika
                            </MetaItem>
                            <MetaItem>
                                <i className="fa-solid fa-at" />
                                info@puhony.eu
                            </MetaItem>
                        </HeaderMeta>
                    </HeaderInfo>
                </Header>

                <Section style={{animationDelay: '0.1s'}}>
                    <SectionLabel>Technologie & skills</SectionLabel>
                    <TagCloud>
                        {skills.map((skill, i) => (
                            <SkillTag key={i}>{skill}</SkillTag>
                        ))}
                    </TagCloud>
                </Section>

                <Section style={{animationDelay: '0.15s'}}>
                    <SectionLabel>Pracovní zkušenosti</SectionLabel>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Freelancer</ItemTitle>
                                <ItemDate>2019 — současnost</ItemDate>
                            </ItemHeader>
                            <ItemRole>Web developer & designer</ItemRole>
                            <ItemDesc>Webové stránky, aplikace a systémy na míru. Spolupráce s firmami i jednotlivci — od návrhu po nasazení.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Nextap</ItemTitle>
                                <ItemDate>2020 — současnost</ItemDate>
                            </ItemHeader>
                            <ItemRole>Frontend developer</ItemRole>
                            <ItemDesc>Node.js, React, Angular, Astro a další. Spolupráce na mezinárodních projektech.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>DAMI development s.r.o.</ItemTitle>
                                <ItemDate>2019 — 2020</ItemDate>
                            </ItemHeader>
                            <ItemRole>Junior webdeveloper</ItemRole>
                            <ItemDesc>PHP (Yii2), JavaScript (React.JS)</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Paiwand — Afghan association London</ItemTitle>
                                <ItemDate>2019</ItemDate>
                            </ItemHeader>
                            <ItemRole>Stáž — Erasmus</ItemRole>
                            <ItemDesc>Práce na interním systému v rámci programu Erasmus.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Tchk, ZŠ BCUO a další</ItemTitle>
                                <ItemDate>2019 — ...</ItemDate>
                            </ItemHeader>
                            <ItemDesc>Spolupráce na menších projektech.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>
                </Section>

                <Section style={{animationDelay: '0.2s'}}>
                    <SectionLabel>Externí spolupráce</SectionLabel>
                    <ProjectGrid>
                        <ProjectItem>
                            <ProjectName>Crowningarts.com</ProjectName>
                            <ProjectSub>Sociální síť pro umělce</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Steller.co</ProjectName>
                            <ProjectSub>Foto/video příběhy pro cestovatele</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>LaserGame-hk.cz</ProjectName>
                            <ProjectSub>Rezervační systém</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Foosapp.eu</ProjectName>
                            <ProjectSub>Aplikace pro stolní fotbálek</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Tanix.cz</ProjectName>
                            <ProjectSub>Monitoring IoT zařízení</ProjectSub>
                        </ProjectItem>
                    </ProjectGrid>
                </Section>

                <Section style={{animationDelay: '0.25s'}}>
                    <SectionLabel>Vzdělání</SectionLabel>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Fakulta informatiky a managmentu UHK</ItemTitle>
                                <ItemDate>2022 — současnost</ItemDate>
                            </ItemHeader>
                            <ItemDesc>Aplikovaná informatika (Bc.)</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>VIVES University — Kortrijk, Belgie</ItemTitle>
                                <ItemDate>09/2024 — 02/2025</ItemDate>
                            </ItemHeader>
                            <ItemRole>Erasmus+</ItemRole>
                            <ItemDesc>Informatics</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>České vysoké učení technické v Praze</ItemTitle>
                                <ItemDate>2021 — 2022</ItemDate>
                            </ItemHeader>
                            <ItemDesc>Webové technologie, programování a algoritmizace, databáze.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>DELTA — SŠIE Pardubice</ItemTitle>
                                <ItemDate>2017 — 2021</ItemDate>
                            </ItemHeader>
                            <ItemRole>Ukončeno maturitou</ItemRole>
                            <ItemDesc>Programování (C#), webové technologie (HTML, CSS, PHP, JS), konfigurace PC, správa databází a sítí, Linux, Adobe.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>
                </Section>

                <DarkSection style={{animationDelay: '0.3s'}}>
                    <SectionLabelLight>Soutěže & ocenění</SectionLabelLight>
                    <AwardList>
                        <AwardItem>
                            <AwardPlace>
                                <i className="fa-solid fa-trophy" />
                            </AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Pardubický talent</AwardTitle>
                                <AwardMeta>Pardubice — Červen 2022</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>
                                <i className="fa-solid fa-award" />
                            </AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Ocenění Pardubického kraje</AwardTitle>
                                <AwardMeta>Spolupráce na projektu BurzaŠkol — Duben 2022</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>2.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Ústřední kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Červenec 2021</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>1.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Krajské kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Duben 2021</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>2.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Hackathon KHK 2020</AwardTitle>
                                <AwardMeta>Královéhradecký kraj & tchk.cz — Říjen 2020</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>
                                <i className="fa-solid fa-award" />
                            </AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Mladý řemeslník roku</AwardTitle>
                                <AwardMeta>SSŠČMS, záštita senátu ČR — Říjen 2019</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>4.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Ústřední kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Červen 2019</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>2.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Krajské kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Duben 2019</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                    </AwardList>
                </DarkSection>

                <FooterSection style={{animationDelay: '0.35s'}}>
                    <LangRow>
                        <LangItem>
                            <LangName>Čeština</LangName>
                        </LangItem>
                        <LangItem>
                            <LangName>Angličtina</LangName>
                        </LangItem>
                    </LangRow>
                </FooterSection>
            </CvCenter>
        </Layout>
    )
}

export default CV

const CvCenter = styled(Center)`
    margin-top: 8em;
`

/* ---- exports used by other components ---- */
export const SectionTitle = styled.h3`
  margin-top: 3em;
  font-size: 1.2em;
  margin-bottom: 0;
  position: relative;
`

export const Paragraph = styled.p`
  margin-top: 0;
  margin-bottom: 2em;
  line-height: 1.8;
`

/* ---- animations ---- */

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/* ---- header ---- */

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 2em;
  margin-bottom: 4em;
  animation: ${fadeUp} 0.5s ease both;

  @media (max-width: 560px) {
    flex-direction: column;
    text-align: center;
  }
`

const Avatar = styled.div`
  width: 8em;
  height: 8em;
  min-width: 8em;
  background-color: #e9e9e9;
  background-image: url("/img/profile.jpg");
  background-size: cover;
  background-position: center;
  border-radius: 50%;
  border: 3px solid #f0f0f0;
`

const HeaderInfo = styled.div``

const Name = styled.h2`
  font-size: 2em;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
`

const Role = styled.div`
  font-size: 1.05em;
  opacity: 0.45;
  font-weight: 300;
  margin-top: 0.2em;
`

const HeaderMeta = styled.div`
  display: flex;
  gap: 1.5em;
  margin-top: 0.8em;

  @media (max-width: 560px) {
    justify-content: center;
  }
`

const MetaItem = styled.div`
  font-size: 0.82em;
  opacity: 0.4;
  display: flex;
  align-items: center;
  gap: 0.45em;

  i {
    font-size: 0.85em;
  }
`

/* ---- sections ---- */

const Section = styled.div`
  margin-bottom: 3.5em;
  animation: ${fadeUp} 0.5s ease both;
`

const SectionLabel = styled.div`
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.8;
  margin-bottom: 1.8em;
  padding-bottom: 0.8em;
  border-bottom: 1px solid #ebebeb;
`

/* ---- skills ---- */

const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
`

const SkillTag = styled.span`
  font-size: 0.82em;
  font-weight: 400;
  background: #f3f3f3;
  border: 1px solid #eaeaea;
  color: rgba(25, 25, 25, 0.65);
  padding: 0.4em 0.9em;
  border-radius: 2em;
  letter-spacing: 0.01em;
  transition: background 0.2s ease;

  &:hover {
    background: #eaeaea;
  }
`

/* ---- timeline ---- */

const TimelineItem = styled.div`
  display: flex;
  gap: 1.2em;
  margin-bottom: 1.8em;
  position: relative;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 1.4em;
    bottom: -0.8em;
    width: 1px;
    background: #e0e0e0;
  }
`

const TimelineDot = styled.div`
  width: 9px;
  height: 9px;
  min-width: 9px;
  border-radius: 50%;
  background: rgba(25, 25, 25, 0.2);
  margin-top: 0.45em;
`

const TimelineContent = styled.div``

const ItemHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1em;
  flex-wrap: wrap;
`

const ItemTitle = styled.div`
  font-weight: 700;
  font-size: 1em;
  letter-spacing: -0.01em;
`

const ItemDate = styled.div`
  font-size: 0.78em;
  opacity: 0.85;
  font-weight: 400;
`

const ItemRole = styled.div`
  font-size: 0.88em;
  opacity: 0.85;
  margin-top: 0.15em;
`

const ItemDesc = styled.div`
  font-size: 0.88em;
  opacity: 0.85;
  font-weight: 300;
  line-height: 1.6;
  margin-top: 0.25em;
  max-width: 36em;
`

/* ---- project grid ---- */

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14em, 1fr));
  gap: 1em;
`

const ProjectItem = styled.div`
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  border-radius: 0.4em;
  padding: 1.2em 1.3em;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

const ProjectName = styled.div`
  font-weight: 700;
  font-size: 0.92em;
  margin-bottom: 0.2em;
`

const ProjectSub = styled.div`
  font-size: 0.8em;
  opacity: 0.45;
  font-weight: 300;
  line-height: 1.5;
`

/* ---- dark awards section ---- */

const DarkSection = styled.div`
  background: rgba(25, 25, 25, 1);
  color: #fff;
  border-radius: 0.6em;
  padding: 2.5em 2.5em;
  margin-bottom: 3.5em;
  animation: ${fadeUp} 0.5s ease both;

  @media (max-width: 560px) {
    padding: 2em 1.5em;
  }
`

const SectionLabelLight = styled.div`
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.35;
  margin-bottom: 2em;
`

const AwardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2em;
`

const AwardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2em;
`

const AwardPlace = styled.div`
  width: 2.2em;
  min-width: 2.2em;
  height: 2.2em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0.35em;
  font-weight: 800;
  font-size: 0.9em;
  opacity: 0.6;

  i {
    font-size: 0.85em;
  }
`

const AwardInfo = styled.div``

const AwardTitle = styled.div`
  font-weight: 600;
  font-size: 0.92em;
  letter-spacing: -0.01em;
`

const AwardMeta = styled.div`
  font-size: 0.78em;
  opacity: 0.35;
  font-weight: 300;
  margin-top: 0.1em;
`

/* ---- languages footer ---- */

const FooterSection = styled.div`
  margin-bottom: 6em;
  animation: ${fadeUp} 0.5s ease both;
`

const LangRow = styled.div`
  display: flex;
  gap: 3em;
`

const LangItem = styled.div``

const LangName = styled.div`
  font-weight: 700;
  font-size: 0.95em;
`

const LangLevel = styled.div`
  font-size: 0.82em;
  opacity: 0.4;
  font-weight: 300;
  margin-top: 0.15em;
`
